import type { Location } from '@corporate/components/LocationCard/LocationCard.vue'
import type { LngLatBounds } from 'mapbox-gl'

/**
 * Check if a location is within the map bounds.
 */
export const isLocationWithinBounds = (location: Location, bounds: LngLatBounds): boolean => {
  if (!location.position) {
    return false
  }
  return bounds.contains([location.position.longitude, location.position.latitude])
}

/**
 * Check if a latitude and longitude are valid.
 * Mapbox requires latitudes to be between -90 and 90 and longitudes to be between -180 and 180.
 */
const isValidLatLng = (lat: number, lng: number): boolean => {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
}

/**
 * Get the furthest north, east, south and west of all locations.
 * This is used to set the initial bounds of the map.
 * The -0.1 and +0.1 are used to add a small padding to the bounds.
 * This prevents the map from being too zoomed in.
 */
export const getAllLocationBounds = async (locations: Location[]) => {
  const filteredLocations = locations.filter(l => l.position && l.position.latitude && l.position.longitude)
  if (filteredLocations.length === 0) {
    return null
  }

  const latitudes = filteredLocations.map(l => l.position?.latitude).filter(v => v !== undefined)
  const longitudes = filteredLocations.map(l => l.position?.longitude).filter(v => v !== undefined)

  const minLatitude = Math.min(...latitudes) - 0.1
  const maxLatitude = Math.max(...latitudes) + 0.1
  const minLongitude = Math.min(...longitudes) - 0.1
  const maxLongitude = Math.max(...longitudes) + 0.1

  if (!isValidLatLng(minLatitude, minLongitude) || !isValidLatLng(maxLatitude, maxLongitude)) {
    return null
  }

  const { LngLatBounds, LngLat } = await import('mapbox-gl')
  return new LngLatBounds(new LngLat(minLongitude, minLatitude), new LngLat(maxLongitude, maxLatitude))
}
